import { useDashboard } from '../hooks/useWpApi';
import { SilviaLoader } from '../components/SilviaLoader';
import { RefreshCw, ShoppingCart, TrendingUp, TrendingDown, DollarSign, AlertTriangle, Clock, Heart, Activity, Eye, Zap, Search, Users, BarChart3, MousePointerClick } from 'lucide-react';
import '../styles/Stats.css';

export function Stats() {
  const { data, loading, error, refresh, lastRefresh } = useDashboard(90_000);

  if (loading && !data) {
    return <SilviaLoader text="Ładowanie danych..." />;
  }

  if (error && !data) {
    return (
      <div className="stats-error">
        <AlertTriangle size={32} />
        <p>{error}</p>
        <p className="stats-error-hint">Sprawdź połączenie sieciowe lub skontaktuj się z administratorem.</p>
        <button className="btn-refresh" onClick={refresh}>Spróbuj ponownie</button>
      </div>
    );
  }

  if (!data) return null;

  const { pending_orders, today, finance, health, rum, sales_comparison, delayed_packages, gsc, ga, complaints_count } = data;
  const allOrdered = pending_orders.total === 0;

  return (
    <div className="stats-page">
      {/* Header */}
      <div className="stats-header">
        <div className="stats-header-top">
          <h1>📊 Dashboard</h1>
          <button className="btn-icon-refresh" onClick={refresh} title="Odśwież">
            <RefreshCw size={18} className={loading ? 'spinning' : ''} />
          </button>
        </div>
        {lastRefresh && (
          <span className="stats-updated">
            Aktualizacja: {lastRefresh.toLocaleTimeString('pl-PL')}
            {data.generated_at && <> · Serwer: {data.generated_at.split(' ')[1]}</>}
          </span>
        )}
        {error && data && (
          <span className="stats-stale-warning">⚠️ Dane mogą być nieaktualne — {error}</span>
        )}
      </div>

      {/* ── GA4 Real-time + GSC Row ── */}
      {(ga || gsc) && (
        <section className="stats-section stats-analytics-row">
          {ga && (
            <div className="analytics-card ga-card">
              <div className="analytics-card-header">
                <Users size={14} />
                <span>Na stronie teraz</span>
              </div>
              <div className="analytics-card-body">
                <span className="analytics-big-number">{ga.active5min}</span>
                <span className="analytics-sub">{ga.active30min} (30 min)</span>
              </div>
              <div className="ga-sparkline">
                {ga.minutesData.slice(-15).map((v, i) => (
                  <div key={i} className="ga-spark-bar" style={{ height: `${Math.max(v * 8, 2)}px` }} />
                ))}
              </div>
            </div>
          )}
          {gsc && (
            <div className="analytics-card gsc-card">
              <div className="analytics-card-header">
                <Search size={14} />
                <span>GSC (7 dni)</span>
              </div>
              <div className="gsc-metrics">
                <div className="gsc-metric">
                  <MousePointerClick size={13} />
                  <span className="gsc-val">{gsc.clicks}</span>
                  <span className="gsc-lbl">kliknięć</span>
                </div>
                <div className="gsc-metric">
                  <Eye size={13} />
                  <span className="gsc-val">{formatNumber(gsc.impressions)}</span>
                  <span className="gsc-lbl">wyświetleń</span>
                </div>
                <div className="gsc-metric">
                  <BarChart3 size={13} />
                  <span className="gsc-val">{gsc.ctr}%</span>
                  <span className="gsc-lbl">CTR</span>
                </div>
                <div className="gsc-metric">
                  <TrendingUp size={13} />
                  <span className={`gsc-val ${gsc.position <= 15 ? 'gsc-good' : gsc.position <= 30 ? 'gsc-warn' : 'gsc-bad'}`}>{gsc.position}</span>
                  <span className="gsc-lbl">pozycja</span>
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* ── Today's Sales Hero ── */}
      <section className="stats-section">
        <h2 className="section-title"><DollarSign size={18} />Dziś</h2>
        <div className="today-hero-card">
          <div className="hero-row">
            <div className="hero-main">
              <span className="hero-number">{today.orders_count}</span>
              <span className="hero-label">zamówień</span>
            </div>
            <div className="hero-amount">{today.orders_amount.toFixed(0)} zł</div>
          </div>
          {today.onhold_count > 0 && (
            <div className="hero-sub">+ {today.onhold_count} on-hold ({today.onhold_amount.toFixed(0)} zł)</div>
          )}
        </div>

        {/* Sales comparison */}
        <div className="comparison-card">
          <div className="comparison-row">
            <span className="comparison-label">📅 Rok temu</span>
            <span className="comparison-value">{sales_comparison.last_year.count} zam. · {sales_comparison.last_year.amount.toFixed(0)} zł</span>
          </div>
          {sales_comparison.change_pct !== 0 && (
            <div className={`comparison-change ${sales_comparison.change_pct > 0 ? 'positive' : 'negative'}`}>
              {sales_comparison.change_pct > 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
              <span>{sales_comparison.change_pct > 0 ? '+' : ''}{sales_comparison.change_pct}% vs rok temu</span>
            </div>
          )}
        </div>
      </section>

      {/* ── Costs & Margin ── */}
      <section className="stats-section">
        <h2 className="section-title"><Activity size={18} />Koszty i marża</h2>
        <div className="costs-summary-card">
          <div className="costs-summary-row">
            <span className="costs-revenue-label">Przychód</span>
            <span className="costs-revenue-val">{today.orders_amount.toFixed(0)} zł</span>
          </div>
          <div className="costs-breakdown">
            <CostRow emoji="📦" label="Produkty dostawcy" amount={today.supplier_cost} />
            {(today.transport_cost ?? 0) > 0 && <CostRow emoji="🚚" label="Transport" amount={today.transport_cost ?? 0} />}
            {(today.prep_cost ?? 0) > 0 && <CostRow emoji="📋" label="Przygotowanie" amount={today.prep_cost ?? 0} />}
            <CostRow emoji="ⓕ" label="Meta Ads" amount={today.meta_ads} />
            <CostRow emoji="G" label="Google Ads" amount={today.google_ads} />
          </div>
          <div className="costs-summary-total">
            <span>Razem koszty</span>
            <span>{today.total_costs.toFixed(0)} zł</span>
          </div>
          <div className={`costs-margin-row ${today.margin >= 0 ? 'positive' : 'negative'}`}>
            <span>💰 Marża</span>
            <span>{today.margin.toFixed(0)} zł ({today.margin_pct}%)</span>
          </div>
        </div>
      </section>

      {/* ── Remaining Orders ── */}
      <section className="stats-section">
        <h2 className="section-title">
          <ShoppingCart size={18} />
          Zamówienia do realizacji
        </h2>
        {allOrdered ? (
          <div className="celebration-card">
            <span className="celebration-emoji">🏝️</span>
            <span>Wszystko zamówione!</span>
          </div>
        ) : (
          <>
            <div className="remaining-total-card">
              <div className="remaining-big-number">{pending_orders.total}</div>
              <div className="remaining-big-label">zamówień • {pending_orders.total_cost.toFixed(0)} zł</div>
            </div>
            <div className="metric-grid cols-3">
              <MetricCard icon="💳" label="Opłacone" value={pending_orders.prepaid} color="blue" />
              <MetricCard icon="🧊" label="Pobranie" value={pending_orders.cod} color="cyan" />
              <MetricCard icon="🚚" label="DHL" value={pending_orders.dhl} color="amber" />
              {pending_orders.low_stock > 0 && (
                <MetricCard icon="🚨" label="Low stock" value={pending_orders.low_stock} color="red" />
              )}
              {pending_orders.complaints > 0 && (
                <MetricCard icon="⚠️" label="Reklamacje" value={pending_orders.complaints} color="orange" />
              )}
              {pending_orders.return_stock > 0 && (
                <MetricCard icon="🏪" label="Ze zwrotów" value={pending_orders.return_stock} color="purple" />
              )}
            </div>
          </>
        )}
        {delayed_packages > 0 && (
          <div className="alert-card warning">
            <Clock size={16} /><span>{delayed_packages} paczek czeka &gt;24h na wysyłkę</span>
          </div>
        )}
        {(complaints_count ?? 0) > 0 && (
          <div className="alert-card complaint">
            <AlertTriangle size={16} /><span>{complaints_count} zamówień z reklamacjami</span>
          </div>
        )}
      </section>

      {/* ── Finance Quick View ── */}
      <section className="stats-section">
        <h2 className="section-title"><DollarSign size={18} />Finanse</h2>
        <div className="metric-grid cols-2">
          <MetricCard icon="🏦" label="Przelew (on-hold)"
            value={`${finance.przelew.count}`}
            subtitle={`${finance.przelew.amount.toFixed(0)} zł`}
            color="lightblue" />
          <MetricCard icon="📬" label="Pobranie (zamrożone)"
            value={`${finance.pobranie.count}`}
            subtitle={`${finance.pobranie.amount.toFixed(0)} zł`}
            color="blue" />
          <MetricCard icon="💵" label="Wypłata dziś"
            value={`${finance.dzis.amount.toFixed(0)} zł`}
            subtitle={`${finance.dzis.count} operacji`}
            color="green" />
          <MetricCard icon="📆" label={finance.jutro.label || 'Jutro'}
            value={`${finance.jutro.amount.toFixed(0)} zł`}
            subtitle={`${finance.jutro.count} operacji`}
            color="teal" />
        </div>

        {/* FB comparison inline */}
        <div className="fb-inline-row">
          <span className="fb-inline-item">📘 FB dziś: <b>{finance.fb_today.toFixed(0)} zł</b></span>
          <span className="fb-inline-item">📅 FB rok temu: <b>{finance.fb_last_year.toFixed(0)} zł</b></span>
        </div>

        {/* Payout forecast */}
        {finance.payout_forecast && finance.payout_forecast.length > 0 && (
          <div className="forecast-strip">
            {finance.payout_forecast.map((day, i) => (
              <div key={i} className="forecast-day">
                <span className="forecast-label">{day.label}</span>
                <span className="forecast-amount">{day.amount.toFixed(0)}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Health Quick View ── */}
      <section className="stats-section">
        <h2 className="section-title"><Heart size={18} />System Health</h2>
        <div className={`health-score-card ${health.score > 80 ? 'good' : health.score > 60 ? 'warn' : 'bad'}`}>
          <div className="health-score-circle">
            <span className="health-score-number">{health.score}</span>
            <span className="health-score-label">%</span>
          </div>
          <span className="health-score-status">{health.status === 'healthy' ? '✅ Zdrowy' : health.status === 'degraded' ? '⚠️ Problemy' : health.status === 'warning' ? '⚠️ Ostrzeżenia' : '❌ Krytyczny'}</span>
        </div>
        {health.checks && Object.keys(health.checks).length > 0 && (
          <div className="health-checks-list">
            {Object.entries(health.checks).map(([key, check]) => (
              <div key={key} className={`health-check-item ${check.status}`}>
                <span className="hc-icon">{check.status === 'ok' ? '✅' : check.status === 'warning' ? '⚠️' : '❌'}</span>
                <span className="hc-label">{check.label}</span>
                {check.value !== null && <span className="hc-value">{check.value}</span>}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── RUM Quick View ── */}
      {rum && rum.total_records > 0 && (
        <section className="stats-section">
          <h2 className="section-title"><Zap size={18} />RUM Performance</h2>
          <div className="rum-summary-row">
            <div className="rum-visitors">
              <Eye size={14} /><span>{rum.visitors} odwiedzających (24h)</span>
            </div>
          </div>
          <div className="metric-grid cols-2">
            {Object.entries(rum.averages).slice(0, 6).map(([type, metric]) => (
              <MetricCard
                key={type}
                icon="⚡"
                label={rumTypeLabel(type)}
                value={`${metric.avg} ms`}
                subtitle={`${metric.cnt} pomiarów`}
                color={metric.avg < 2000 ? 'green' : metric.avg < 4000 ? 'amber' : 'red'}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ── Helper components ─────────────────────────────────────────────────────

function MetricCard({ icon, label, value, subtitle, color = 'gray' }: {
  icon: string;
  label: string;
  value: string | number;
  subtitle?: string;
  color?: string;
}) {
  return (
    <div className={`metric-card color-${color}`}>
      <div className="metric-icon">{icon}</div>
      <div className="metric-body">
        <div className="metric-value">{value}</div>
        <div className="metric-label">{label}</div>
        {subtitle && <div className="metric-subtitle">{subtitle}</div>}
      </div>
    </div>
  );
}

function CostRow({ emoji, label, amount }: { emoji: string; label: string; amount: number }) {
  return (
    <div className="cost-breakdown-row">
      <span className="cost-row-icon">{emoji}</span>
      <span className="cost-row-label">{label}</span>
      <span className="cost-row-amount">{amount.toFixed(0)} zł</span>
    </div>
  );
}

function formatNumber(n: number): string {
  if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
  return String(n);
}

function rumTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    'page_load': 'Page Load',
    'fcp': 'FCP',
    'lcp': 'LCP',
    'cls': 'CLS',
    'fid': 'FID',
    'inp': 'INP',
    'ttfb': 'TTFB',
    'dom_interactive': 'DOM Int.',
    'dom_complete': 'DOM Complete',
    'page_home': 'Strona gł.',
    'page_product': 'Produkt',
    'page_cart': 'Koszyk',
    'page_checkout': 'Checkout',
    'page_thankyou': 'Dziękujemy',
    'atc_sidecart': 'ATC Sidecart',
    'checkout_full': 'Checkout Full',
  };
  return labels[type] || type;
}
